import type { GameConfig, PipeSnapshot } from './GameState.ts';
import type { Rng } from './Rng.ts';

export interface Pipe {
  id: number;
  x: number;
  gapTop: number;
  gapBottom: number;
  scored: boolean;
}

/**
 * Pipe field. The RNG is consumed at exactly one place -- pipe spawning -- so
 * the pipe layout for a given seed is independent of how the bird was flown.
 * Two runs with the same seed therefore face an identical course, which is what
 * makes the controller comparisons in `experiments/TrialRunner.ts` fair.
 */
export class PipeField {
  private pipes: Pipe[] = [];
  private nextId = 0;
  /** Centre of the most recently spawned gap, for `maxGapCenterStep`. */
  private lastCenter: number | null = null;

  constructor(
    private readonly config: GameConfig,
    private readonly rng: Rng,
  ) {
    this.reset();
  }

  reset(): void {
    this.pipes = [];
    this.nextId = 0;
    this.lastCenter = null;
    this.spawn(this.config.width + this.config.firstPipeOffset);
  }

  private spawn(x: number): void {
    const { height, gapMargin, pipeGap, maxGapCenterStep } = this.config;
    const minCenter = gapMargin + pipeGap / 2;
    const maxCenter = height - gapMargin - pipeGap / 2;

    // Draw the centre from the full range, then pull it back towards the
    // previous gap if the step would be too large. Clamping rather than
    // re-drawing keeps exactly one RNG call per pipe, so the course stays
    // reproducible in the same number of draws regardless of the limit.
    let center = this.rng.range(minCenter, maxCenter);
    if (maxGapCenterStep > 0 && this.lastCenter !== null) {
      const lo = Math.max(minCenter, this.lastCenter - maxGapCenterStep);
      const hi = Math.min(maxCenter, this.lastCenter + maxGapCenterStep);
      center = Math.max(lo, Math.min(hi, center));
    }
    this.lastCenter = center;

    this.pipes.push({
      id: this.nextId++,
      x,
      gapTop: center - pipeGap / 2,
      gapBottom: center + pipeGap / 2,
      scored: false,
    });
  }

  /** Advance one fixed step; spawn/retire pipes as needed. */
  update(dt: number): void {
    const { pipeSpeed, pipeSpacing, pipeWidth, width } = this.config;
    for (const pipe of this.pipes) pipe.x -= pipeSpeed * dt;

    const last = this.pipes[this.pipes.length - 1];
    if (last && last.x <= width - pipeSpacing) {
      this.spawn(last.x + pipeSpacing);
    }
    while (this.pipes.length > 0 && this.pipes[0].x + pipeWidth < -pipeWidth) {
      this.pipes.shift();
    }
  }

  /** The nearest pipe the bird has not yet cleared. */
  next(birdX: number, birdRadius: number): Pipe | undefined {
    return this.pipes.find((p) => p.x + this.config.pipeWidth >= birdX - birdRadius);
  }

  all(): readonly Pipe[] {
    return this.pipes;
  }

  snapshot(): PipeSnapshot[] {
    return this.pipes.map((p) => ({ ...p }));
  }
}
